/**
 * GameRecordingForm - Form for adding game recordings with full team info
 * Extracted from RecordingsTab.jsx
 */
import { motion } from "framer-motion";
import { Video, X, Users, Wand2 } from "lucide-react";
import { Button } from "../../ui/button";
import { ImageUpload } from "../ImageUpload";

const SPORTS_LIST = [
  "Football", "Basketball", "Boys Basketball", "Girls Basketball", 
  "Mens Basketball", "Womens Basketball", "Soccer", "Baseball", 
  "Softball", "Volleyball", "Wrestling", "Tennis", "Golf", 
  "Track & Field", "Swimming", "Lacrosse", "Hockey"
];

// Team input section component
const TeamInputSection = ({ 
  label, 
  team, 
  onTeamChange, 
  teams, 
  onSelectTeam, 
  onExtractColor, 
  extractingColor,
  idPrefix 
}) => (
  <div className="p-4 bg-zinc-800/50 border border-white/5">
    <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400 mb-3 flex items-center gap-2">
      <Users className="w-4 h-4" /> {label} *
    </h4>
    <div className="space-y-3">
      <div>
        <label className="block text-[10px] text-zinc-500 mb-1">Quick Select from Team Manager</label>
        <select 
          onChange={(e) => e.target.value && onSelectTeam(e.target.value)}
          className="w-full bg-zinc-900 border border-orange-500/30 text-white px-3 py-2 text-sm"
          value=""
        >
          <option value="">-- Select Team --</option>
          {teams.map(t => (
            <option key={t.id} value={t.id}>
              {t.name} {t.mascot ? `(${t.mascot})` : ''} - {t.sport}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input 
          type="text" 
          value={team.name} 
          onChange={(e) => onTeamChange({ ...team, name: e.target.value })} 
          placeholder="Team Name" 
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
        />
        <input 
          type="text" 
          value={team.abbreviation} 
          onChange={(e) => onTeamChange({ ...team, abbreviation: e.target.value })} 
          placeholder="Abbreviation" 
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
        />
      </div>
      <ImageUpload
        value={team.logo_url}
        onChange={(url) => onTeamChange({ ...team, logo_url: url })}
        label="Team Logo"
        placeholder="Logo URL"
        previewSize="sm"
        id={`${idPrefix}-team-logo`}
      />
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className="block text-[10px] text-zinc-500 mb-1">Team Color</label>
          <div className="flex items-center gap-2">
            <input 
              type="color" 
              value={team.color} 
              onChange={(e) => onTeamChange({ ...team, color: e.target.value })} 
              className="w-10 h-8 bg-transparent border-0 cursor-pointer"
            />
            <input 
              type="text" 
              value={team.color} 
              onChange={(e) => onTeamChange({ ...team, color: e.target.value })} 
              className="flex-1 bg-zinc-900 border border-white/10 text-white px-2 py-1 text-xs font-mono" 
            />
            {team.logo_url && (
              <button 
                type="button"
                onClick={onExtractColor}
                disabled={extractingColor}
                className="text-xs text-orange-500 hover:text-orange-400 whitespace-nowrap"
              >
                {extractingColor ? "..." : "Auto"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const GameRecordingForm = ({ 
  gameForm, 
  setGameForm, 
  teams,
  onSelectTeam,
  onExtractColor,
  extractingColor,
  onGenerateThumbnail,
  generatingThumbnail,
  onSave, 
  onClose, 
  saving 
}) => (
  <motion.div 
    initial={{ opacity: 0, y: -10 }} 
    animate={{ opacity: 1, y: 0 }} 
    className="p-6 bg-zinc-900/50 border border-orange-500/30 space-y-6"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Video className="w-5 h-5 text-orange-500" />
        <div>
          <h3 className="font-secondary text-lg font-bold uppercase text-white">Add Game Recording</h3>
          <p className="text-xs text-zinc-500">Add a video link with full game info (teams, thumbnail, etc.)</p>
        </div>
      </div>
      <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
        <X className="w-5 h-5" />
      </button>
    </div>
    
    {/* Video URL - Most Important */}
    <div className="bg-orange-500/10 border border-orange-500/30 p-4">
      <label className="block text-xs font-bold uppercase tracking-widest text-orange-400 mb-2">Video URL (MP4 or Stream Link) *</label>
      <input 
        type="url" 
        value={gameForm.video_url} 
        onChange={(e) => setGameForm({ ...gameForm, video_url: e.target.value })} 
        placeholder="https://antlersn.tv/upload/TKDS_STREAMS/ASN2/ABC_TXWes_Oct4.mp4" 
        className="w-full bg-zinc-800 border border-orange-500/50 text-white px-3 py-3 text-sm font-mono"
        data-testid="video-url-input"
      />
    </div>
    
    {/* Broadcast Title */}
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
        Broadcast Title (Optional)
      </label>
      <input 
        type="text" 
        value={gameForm.broadcast_title} 
        onChange={(e) => setGameForm({ ...gameForm, broadcast_title: e.target.value })} 
        placeholder="e.g., Week 5, Championship Game, Season Opener" 
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm md:w-1/2"
        data-testid="broadcast-title-input"
      />
      <p className="text-[10px] text-zinc-600 mt-1">
        Final title format: <span className="text-zinc-400">{gameForm.broadcast_title ? `"${gameForm.broadcast_title} - ` : '"'}{gameForm.home_team.name || 'Home Team'} vs. {gameForm.away_team.name || 'Away Team'}{gameForm.broadcast_title ? '"' : '"'}</span>
      </p>
    </div>
    
    {/* Basic Info Row */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Sport *</label>
        <select 
          value={gameForm.sport} 
          onChange={(e) => setGameForm({ ...gameForm, sport: e.target.value })} 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        >
          {SPORTS_LIST.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Game Date *</label>
        <input 
          type="date" 
          value={gameForm.recorded_at} 
          onChange={(e) => setGameForm({ ...gameForm, recorded_at: e.target.value })} 
          required 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Stats Link (Optional)</label>
        <input 
          type="url" 
          value={gameForm.stats_link} 
          onChange={(e) => setGameForm({ ...gameForm, stats_link: e.target.value })} 
          placeholder="https://statmoose.com/..." 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
        />
      </div>
    </div>
    
    {/* Teams Row */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <TeamInputSection
        label="Home Team"
        team={gameForm.home_team}
        onTeamChange={(team) => setGameForm({ ...gameForm, home_team: team })}
        teams={teams}
        onSelectTeam={(id) => onSelectTeam(id, true)}
        onExtractColor={() => onExtractColor(true)}
        extractingColor={extractingColor.home}
        idPrefix="home"
      />
      <TeamInputSection
        label="Away Team"
        team={gameForm.away_team}
        onTeamChange={(team) => setGameForm({ ...gameForm, away_team: team })}
        teams={teams}
        onSelectTeam={(id) => onSelectTeam(id, false)}
        onExtractColor={() => onExtractColor(false)}
        extractingColor={extractingColor.away}
        idPrefix="away"
      />
    </div>
    
    {/* Scores Row */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Home Score</label>
        <input 
          type="number" 
          value={gameForm.final_home_score} 
          onChange={(e) => setGameForm({ ...gameForm, final_home_score: e.target.value })} 
          placeholder="--" 
          min="0"
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm text-center font-bold text-lg" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Away Score</label>
        <input 
          type="number" 
          value={gameForm.final_away_score} 
          onChange={(e) => setGameForm({ ...gameForm, final_away_score: e.target.value })} 
          placeholder="--" 
          min="0"
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm text-center font-bold text-lg" 
        />
      </div>
      <div className="col-span-2">
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description (Optional)</label>
        <input 
          type="text" 
          value={gameForm.description} 
          onChange={(e) => setGameForm({ ...gameForm, description: e.target.value })} 
          placeholder="Game description or notes..." 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
        />
      </div>
    </div>
    
    {/* Thumbnail Row */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Thumbnail</label>
        <ImageUpload
          value={gameForm.thumbnail_url}
          onChange={(url) => setGameForm({ ...gameForm, thumbnail_url: url })}
          label=""
          placeholder="Thumbnail URL or upload"
          previewSize="lg"
          id="game-thumbnail"
        />
      </div>
      <div className="flex flex-col justify-center">
        <p className="text-xs text-zinc-500 mb-3">
          Generate a thumbnail automatically using the team logos and colors, or upload/paste a URL above.
        </p>
        <Button
          type="button"
          onClick={onGenerateThumbnail}
          disabled={generatingThumbnail || !gameForm.home_team.logo_url || !gameForm.away_team.logo_url}
          className="flex items-center justify-center gap-2 bg-purple-600 text-white hover:bg-purple-700 font-secondary text-xs px-4 py-3 h-auto rounded-none disabled:opacity-50"
        >
          <Wand2 className="w-4 h-4" />
          {generatingThumbnail ? "Generating..." : "Auto-Generate Thumbnail"}
        </Button>
        {(!gameForm.home_team.logo_url || !gameForm.away_team.logo_url) && (
          <p className="text-[10px] text-zinc-600 mt-2">Add team logos first to enable auto-generation</p>
        )}
      </div>
    </div>
    
    {/* Publish Toggle */}
    <div className="flex items-center gap-3">
      <input 
        type="checkbox" 
        id="is_published" 
        checked={gameForm.is_published} 
        onChange={(e) => setGameForm({ ...gameForm, is_published: e.target.checked })}
        className="w-4 h-4"
      />
      <label htmlFor="is_published" className="text-sm text-zinc-400">
        Publish immediately to On Demand page
      </label>
    </div>
    
    {/* Actions */}
    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
      <Button
        type="button"
        onClick={onClose}
        className="bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-6 py-2 h-auto rounded-none"
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="bg-orange-600 text-white hover:bg-orange-700 font-secondary font-bold text-xs px-6 py-2 h-auto rounded-none"
        data-testid="save-game-recording-btn"
      >
        {saving ? "Saving..." : "Add Recording"}
      </Button>
    </div>
  </motion.div>
);

export default GameRecordingForm;
